import { Observable } from 'lib0/observable';
import * as awarenessProtocol from 'y-protocols/awareness';
import * as Y from 'yjs';

import {
	CollaborativeDocumentInitialState,
	CollaborativeDocumentMessage,
	CollaborativeDocumentSyncUpdate,
	CollaborativeUser,
} from '../../../types/collaborative-document';

/**
 * ActionCable Provider for YJS Collaborative Documents
 *
 * Bridges YJS documents with Rails ActionCable WebSockets for real-time collaboration.
 *
 * NOTE: This is a RELAY-based provider, NOT a y-websocket sync protocol implementation.
 *
 * Backend behavior:
 * - Automatically sends initial_state when client subscribes (proactive, no request needed)
 * - Relays base64-encoded YJS updates between clients
 * - Persists collaboration_state for reconnection (when full state + vector + content sent)
 * - Does NOT implement sync_step_1/sync_step_2 protocol
 *
 * Update Strategy:
 * - Incremental updates (every keystroke): Broadcast only, no persistence
 * - Periodic snapshots (every 2-3s): Broadcast + persist to database
 */

export interface ActionCableProviderOptions {
	/** The ActionCable consumer instance */
	consumer: any;
	/** The channel name to subscribe to (default: 'CollaborativeDocumentChannel') */
	channel?: string;
	/** The document ID to collaborate on */
	documentId: string;
	/** Additional channel parameters */
	channelParams?: Record<string, any>;
	/** Current user information for awareness (presence tracking) */
	user?: CollaborativeUser;
	/** Whether to connect immediately */
	connect?: boolean;
	/** Maximum number of reconnection attempts */
	maxReconnectAttempts?: number;
	/** Delay between reconnection attempts (ms) */
	reconnectDelay?: number;
}

export class ActionCableProvider extends Observable<any> {
	public doc: Y.Doc;
	public awareness: awarenessProtocol.Awareness;
	public initialStatePayload: CollaborativeDocumentInitialState | null = null;

	private consumer: any;
	private subscription: any = null;
	private channel: string;
	private documentId: string;
	private channelParams: Record<string, any>;
	private connected: boolean = false;
	private receivedInitialState: boolean = false;
	private synced: boolean = false;
	private reconnecting: boolean = false;
	private reconnectAttempts: number = 0;
	private maxReconnectAttempts: number;
	private reconnectDelay: number;
	private reconnectTimeout: any = null;
	private syncInterval: any = null;

	// Track pending updates to send once connected
	private updateQueue: Uint8Array[] = [];

	constructor(doc: Y.Doc, options: ActionCableProviderOptions) {
		super();

		this.doc = doc;
		this.consumer = options.consumer;
		this.channel = options.channel || 'CollaborativeDocumentChannel';
		this.documentId = options.documentId;
		this.channelParams = options.channelParams || {};
		this.maxReconnectAttempts = options.maxReconnectAttempts ?? 10;
		this.reconnectDelay = options.reconnectDelay ?? 1000;

		// Create awareness instance (required by TipTap CollaborationCursor extension)
		this.awareness = new awarenessProtocol.Awareness(doc);

		// Set user info for local awareness (shows your own cursor color)
		if (options.user) {
			this.awareness.setLocalStateField('user', options.user);
		}

		// Handle local document updates - relay to other clients
		this.doc.on('update', this.handleDocumentUpdate);

		// Handle awareness updates (cursor position, selection, etc.)
		this.awareness.on('update', this.handleAwarenessUpdate);

		// Connect if requested
		if (options.connect !== false) {
			this.connect();
		}
	}

	/**
	 * Connect to the ActionCable channel
	 */
	public connect(): void {
		if (this.subscription || this.reconnecting) {
			return;
		}

		this.reconnecting = true;

		// Generate identifier for this channel+params combination
		const identifier = JSON.stringify({
			channel: this.channel,
			document_id: this.documentId,
			...this.channelParams,
		});

		// Check if a subscription with this identifier already exists (React Strict Mode can cause duplicates)
		const existingSubscription = this.consumer.subscriptions.subscriptions.find((sub: any) => sub.identifier === identifier);

		if (existingSubscription) {
			// Reuse the existing subscription but update callbacks to point to THIS instance
			this.subscription = existingSubscription;
			this.subscription.connected = this.handleConnected;
			this.subscription.disconnected = this.handleDisconnected;
			this.subscription.received = this.handleReceived;
			this.subscription.rejected = this.handleRejected;

			this.reconnecting = false;

			// If already connected, trigger connected callback
			if (this.consumer.connection?.isOpen?.()) {
				this.handleConnected();
			}

			return;
		}

		try {
			this.subscription = this.consumer.subscriptions.create(
				{
					channel: this.channel,
					document_id: this.documentId,
					...this.channelParams,
				},
				{
					connected: this.handleConnected,
					disconnected: this.handleDisconnected,
					received: this.handleReceived,
					rejected: this.handleRejected,
				}
			);
		} catch (error) {
			console.error('[ActionCableProvider] Connection error:', error);
			this.reconnecting = false;
			this.scheduleReconnect();
		}
	}

	/**
	 * Disconnect from the ActionCable channel
	 */
	public disconnect(): void {
		this.connected = false;
		this.synced = false;

		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}

		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	/**
	 * Destroy the provider and clean up
	 */
	public destroy(): void {
		this.stopPeriodicSync();
		this.disconnect();

		this.doc.off('update', this.handleDocumentUpdate);
		this.awareness.off('update', this.handleAwarenessUpdate);

		awarenessProtocol.removeAwarenessStates(this.awareness, [this.doc.clientID], 'provider destroyed');
		this.awareness.destroy();

		this.emit('destroy', []);
	}

	/**
	 * Handle successful connection
	 *
	 * Note: Backend automatically sends initial_state on subscribe, so it may
	 * arrive before or after this callback fires. Our checkAndEmitSynced logic
	 * handles both orderings correctly.
	 */
	private handleConnected = (): void => {
		this.connected = true;
		this.reconnecting = false;
		this.reconnectAttempts = 0;

		// Flush any queued updates from offline editing
		this.flushUpdateQueue();

		// Start periodic sync check (every 30 seconds)
		this.startPeriodicSync();

		this.emit('status', [{ status: 'connected' }]);

		// Check if we can mark as synced (both connected AND received initial state)
		// Backend sends initial_state automatically, so it may have already arrived
		this.checkAndEmitSynced();
	};

	/**
	 * Handle disconnection
	 */
	private handleDisconnected = (): void => {
		const wasConnected = this.connected;
		this.connected = false;
		this.receivedInitialState = false;
		this.synced = false;
		this.reconnecting = false;
		this.subscription = null;

		// Stop periodic sync while disconnected
		this.stopPeriodicSync();

		if (wasConnected) {
			this.emit('status', [{ status: 'disconnected' }]);
			this.scheduleReconnect();
		}
	};

	/**
	 * Handle connection rejection
	 */
	private handleRejected = (): void => {
		console.error('[ActionCableProvider] Connection REJECTED - channel may not exist or authorization failed');
		console.error('[ActionCableProvider] Channel:', this.channel);
		console.error('[ActionCableProvider] Document ID:', this.documentId);
		console.error('[ActionCableProvider] Params:', this.channelParams);

		this.connected = false;
		this.reconnecting = false;
		this.subscription = null;

		this.emit('connection-error', [new Error('Connection rejected')]);
	};

	/**
	 * Check if we're ready to emit synced event
	 * Only emit when BOTH connected AND received initial state
	 */
	private checkAndEmitSynced(): void {
		if (this.connected && this.receivedInitialState && !this.synced) {
			this.synced = true;
			this.emit('synced', [{ synced: true }]);
		}
	}

	/**
	 * Handle incoming messages from ActionCable
	 *
	 * Backend sends two event types:
	 * - initial_state: Sent automatically on subscribe (state, content, state_vector)
	 * - sync_update: Relayed updates from other clients (update: base64 YJS binary)
	 */
	private handleReceived = (data: CollaborativeDocumentMessage): void => {
		const eventType = data.event;
		const payload = data.data;

		if (eventType === 'initial_state') {
			const initialState = payload as CollaborativeDocumentInitialState;

			// Store payload for components that need fallback content
			this.initialStatePayload = initialState;
			this.receivedInitialState = true;

			// Apply persisted YJS state if available
			if (initialState.state) {
				try {
					const state = this.decodeBase64(initialState.state);
					Y.applyUpdate(this.doc, state, this);
				} catch (e) {
					console.error('[ActionCableProvider] Failed to apply YJS state:', e);
				}
			}

			// Check if we can mark as synced (both connected AND received initial state)
			this.checkAndEmitSynced();
		} else if (eventType === 'sync_update') {
			const update = payload as CollaborativeDocumentSyncUpdate;

			// Apply YJS update from another client (relayed through backend)
			if (update.update) {
				try {
					const decoded = this.decodeBase64(update.update);
					Y.applyUpdate(this.doc, decoded, this);
				} catch (e) {
					console.error('[ActionCableProvider] Failed to apply sync_update:', e);
				}
			}
		} else if (eventType === 'awareness_update') {
			// Apply awareness update from another client (cursor position, selection, etc.)
			if (payload.update) {
				try {
					const decoded = this.decodeBase64(payload.update);
					awarenessProtocol.applyAwarenessUpdate(this.awareness, decoded, this);
				} catch (e) {
					console.error('[ActionCableProvider] Failed to apply awareness_update:', e);
				}
			}
		} else if (eventType === 'user_disconnected') {
			// YJS awareness automatically cleans up disconnected users
			// This event is informational - can be used for notifications
			this.emit('user-disconnected', [{ userId: payload.user_id }]);
		} else {
			console.warn('[ActionCableProvider] Unknown event type:', eventType);
		}
	};

	/**
	 * Handle local document updates - send to backend for relay to other clients
	 */
	private handleDocumentUpdate = (update: Uint8Array, origin: any): void => {
		// Don't send updates that came from the network (origin === this)
		if (origin === this) {
			return;
		}

		// Send local updates to backend for relay to other clients
		if (this.connected && this.subscription) {
			try {
				const encoded = this.encodeBase64(update);
				this.subscription.perform('sync_update', { update: encoded });
			} catch (error) {
				console.error('[ActionCableProvider] Error sending update:', error);
				// Queue for retry
				this.updateQueue.push(update);
			}
		} else {
			// Queue for later if not connected
			this.updateQueue.push(update);
		}
	};

	/**
	 * Handle awareness updates (cursor position, selection, user presence)
	 */
	private handleAwarenessUpdate = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }): void => {
		const changedClients = added.concat(updated).concat(removed);

		// Encode awareness state for changed clients
		const awarenessUpdate = awarenessProtocol.encodeAwarenessUpdate(this.awareness, changedClients);

		// Send awareness updates to backend for relay to other clients
		if (this.connected && this.subscription) {
			try {
				const encoded = this.encodeBase64(awarenessUpdate);
				this.subscription.perform('awareness_update', { update: encoded });
			} catch (error) {
				console.error('[ActionCableProvider] Error sending awareness update:', error);
			}
		}
	};

	/**
	 * Send periodic snapshot to backend for persistence
	 * Should be called periodically (every 2-3 seconds) with all 3 fields
	 */
	public sendPersistSnapshot(htmlContent: string): void {
		if (!this.connected || !this.subscription) {
			return;
		}

		try {
			const state = Y.encodeStateAsUpdate(this.doc);
			const stateVector = Y.encodeStateVector(this.doc);

			this.subscription.perform('sync_update', {
				state: this.encodeBase64(state),
				state_vector: this.encodeBase64(stateVector),
				content: htmlContent,
			});
		} catch (error) {
			console.error('[ActionCableProvider] Error sending persist snapshot:', error);
		}
	}

	/**
	 * Flush queued updates
	 */
	private flushUpdateQueue(): void {
		while (this.updateQueue.length > 0 && this.connected && this.subscription) {
			const update = this.updateQueue.shift();
			if (update) {
				try {
					const encoded = this.encodeBase64(update);
					this.subscription.perform('sync_update', { update: encoded });
				} catch (error) {
					console.error('[ActionCableProvider] Error flushing update:', error);
					// Put it back at the front
					this.updateQueue.unshift(update);
					break;
				}
			}
		}
	}

	/**
	 * Start periodic sync to catch missed updates
	 * Requests full state from server every 30 seconds
	 */
	private startPeriodicSync(): void {
		// Clear any existing interval
		this.stopPeriodicSync();

		// Request state every 30 seconds to ensure sync
		this.syncInterval = setInterval(() => {
			if (this.connected && this.subscription) {
				this.subscription.perform('request_state');
			}
		}, 30000); // 30 seconds
	}

	/**
	 * Stop periodic sync
	 */
	private stopPeriodicSync(): void {
		if (this.syncInterval) {
			clearInterval(this.syncInterval);
			this.syncInterval = null;
		}
	}

	/**
	 * Schedule a reconnection attempt
	 */
	private scheduleReconnect(): void {
		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.error('[ActionCableProvider] Max reconnection attempts reached');
			this.emit('connection-error', [new Error('Max reconnection attempts reached')]);
			return;
		}

		this.reconnectAttempts++;
		const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

		this.reconnectTimeout = setTimeout(() => {
			this.reconnectTimeout = null;
			this.reconnecting = false; // Reset so connect() can proceed
			this.connect();
		}, delay);
	}

	/**
	 * Encode Uint8Array as base64 for ActionCable transport
	 */
	private encodeBase64(data: Uint8Array): string {
		return btoa(String.fromCharCode(...data));
	}

	/**
	 * Decode base64 string to Uint8Array
	 */
	private decodeBase64(encoded: string): Uint8Array {
		const binary = atob(encoded);
		const bytes = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i++) {
			bytes[i] = binary.charCodeAt(i);
		}
		return bytes;
	}

	get isConnected(): boolean {
		return this.connected;
	}

	get isReconnecting(): boolean {
		return this.reconnecting;
	}

	get isSynced(): boolean {
		return this.synced;
	}
}
